import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { OrganisationDetail, EntityStatus, CrudEventEnum, CrudActions, EntityTypeEnum, WorkflowActions } from 'api'
import { Locale, Translation } from 'locale'
import { SplitButtonDropdown, ActionItem } from 'view/components/menus'
import { NotificationActions, ShowDialogArgs, NotificationType } from 'store/notification'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { OrganisationDialog } from '../OrganisationDialog'
import { openOrganisationIdsSelector } from 'selectors/selectionSelectors'
import { SelectionActions } from 'store/selection'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { ViewOrganisationDialog } from '../OrganisationDialog/ViewOrganisationDialog'

export type OrganisationActionsButtonProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps, ActionsButtonProps<OrganisationDetail> {}

interface StateProps {
  openOrganisationIds: string[]
}

interface DispatchProps {
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
  onShowOrganisationEntityDialog: (
    entityTypeEnum: EntityTypeEnum,
    entityId: string,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
  openOrganisationProfiles: (organisationIds: string[]) => unknown
  closeOrganisationProfiles: (organisationIds: string[]) => unknown
}

const mapStateToProps = createStructuredSelector({
  openOrganisationIds: openOrganisationIdsSelector
})

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog,
  onShowOrganisationEntityDialog: WorkflowActions.createEditWorkflow,
  openOrganisationProfiles: SelectionActions.openOrganisationProfiles,
  closeOrganisationProfiles: SelectionActions.closeOrganisationProfiles
}

export class OrganisationActionsButtonComponent extends React.Component<OrganisationActionsButtonProps> {
  render() {
    const openOrgs = this.props.items.filter(org => this.canOpenProfile(org))
    const closeOrgs = this.props.items.filter(org => this.canCloseProfile(org))
    const viewOrgs = this.props.items.filter(org => !this.canEditDetails(org))
    const editOrgs = this.props.items.filter(org => this.canEditDetails(org))
    const suspendOrgs = this.props.items.filter(org => this.canSuspend(org))
    const activateOrgs = this.props.items.filter(org => this.canActivate(org))

    const actions: ActionItem[] = [
      {
        id: Translation.TC_ACTION_OPEN_PROFILE,
        label: this.getLabel(Translation.TC_ACTION_OPEN_PROFILE, openOrgs.length),
        onClick: () => this.handleOpenOrgProfiles(openOrgs),
        enabled: openOrgs.length > 0
      },
      {
        id: Translation.TC_ACTION_CLOSE_PROFILE,
        label: this.getLabel(Translation.TC_ACTION_CLOSE_PROFILE, closeOrgs.length),
        onClick: () => this.handleCloseOrgProfiles(closeOrgs),
        enabled: closeOrgs.length > 0
      },
      {
        id: Translation.TC_ACTION_VIEW_DETAILS,
        label: this.getLabel(Translation.TC_ACTION_VIEW_DETAILS, 1),
        onClick: () => this.handleViewOrgDialog(viewOrgs),
        enabled: viewOrgs.length === 1
      },
      {
        id: Translation.TC_ACTION_EDIT_DETAILS,
        label: this.getLabel(Translation.TC_ACTION_EDIT_DETAILS, 1),
        onClick: () => this.handleShowOrgDialog(editOrgs),
        enabled: editOrgs.length === 1
      },
      {
        id: Translation.TC_ACTION_SUSPEND_TRADING,
        label: this.getLabel(Translation.TC_ACTION_SUSPEND_TRADING, suspendOrgs.length),
        onClick: () => this.handleSuspendOrgs(suspendOrgs),
        enabled: suspendOrgs.length > 0
      },
      {
        id: Translation.TC_ACTION_ACTIVATE_TRADING,
        label: this.getLabel(Translation.TC_ACTION_ACTIVATE_TRADING, activateOrgs.length),
        onClick: () => this.handleActivateOrgs(activateOrgs),
        enabled: activateOrgs.length > 0
      }
    ]

    return <SplitButtonDropdown dataTestId={'OrganisationActionsButton'} actions={actions} maxButtons={1} />
  }

  private getLabel = (baseLabel: Translation, count: number) => {
    const { isGroupRow } = this.props
    const countLabel = isGroupRow ? ` (${count})` : ``
    return `${Locale.translate(baseLabel)}${countLabel}`
  }

  private handleOpenOrgProfiles(openOrgs: OrganisationDetail[]) {
    this.props.openOrganisationProfiles(openOrgs.map(org => org.organisation.id))
  }

  private handleCloseOrgProfiles(closeOrgs: OrganisationDetail[]) {
    this.props.closeOrganisationProfiles(closeOrgs.map(org => org.organisation.id))
  }

  private handleShowOrgDialog = (orgs: OrganisationDetail[]) => {
    const correlatedResponseCallback = (workflowId: string) => {
      this.props.showModal(<OrganisationDialog workflowId={workflowId} organisationEntityId={orgs[0].organisation.id} />)
    }
    this.props.onShowOrganisationEntityDialog(EntityTypeEnum.ORGANISATION, orgs[0].organisation.id, correlatedResponseCallback)
  }

  private handleViewOrgDialog = (orgs: OrganisationDetail[]) => {
    this.props.showModal(<ViewOrganisationDialog organisationEntityId={orgs[0].organisation.id} />)
  }

  private handleSuspendOrgs(suspendOrgs: OrganisationDetail[]) {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_TRADING)

    const messageText =
      suspendOrgs.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_SINGLE_ORG, { name: suspendOrgs[0].organisation.legalName })
        : Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_MULTI_ORG, { count: suspendOrgs.length })

    const suspendActions = suspendOrgs.map(suspendOrg =>
      CrudActions.organisationUpsert({
        ...suspendOrg.organisation,
        primeBrokers: suspendOrg.primeBrokers,
        status: EntityStatus.DISABLED,
        ignorePrimeBrokersToDelete: false
      })
    )

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: suspendActions
      }
    })
  }

  private handleActivateOrgs(activateOrgs: OrganisationDetail[]) {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_TRADING)

    const messageText =
      activateOrgs.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_SINGLE_ORG, { name: activateOrgs[0].organisation.legalName })
        : Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_MULTI_ORG, { count: activateOrgs.length })

    const suspendActions = activateOrgs.map(suspendOrg =>
      CrudActions.organisationUpsert({
        ...suspendOrg.organisation,
        primeBrokers: suspendOrg.primeBrokers,
        status: EntityStatus.ENABLED,
        ignorePrimeBrokersToDelete: false
      })
    )

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: titleText,
      messageText: messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: suspendActions
      }
    })
  }

  private canOpenProfile(orgDetail: OrganisationDetail) {
    return !this.props.openOrganisationIds.includes(orgDetail.organisation.id)
  }

  private canCloseProfile(orgDetail: OrganisationDetail) {
    return this.props.openOrganisationIds.includes(orgDetail.organisation.id)
  }

  private canEditDetails(orgDetail: OrganisationDetail) {
    return orgDetail.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  private canSuspend(orgDetail: OrganisationDetail) {
    return orgDetail.organisation.status !== EntityStatus.DISABLED && orgDetail.availableEvents.includes(CrudEventEnum.UPSERT)
  }

  private canActivate(orgDetail: OrganisationDetail) {
    return orgDetail.organisation.status !== EntityStatus.ENABLED && orgDetail.availableEvents.includes(CrudEventEnum.UPSERT)
  }
}

export const OrganisationActionsButton = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(OrganisationActionsButtonComponent)
)
